import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { getAllCategories, getTeamsByCategory } from "../utils/categoryTeams";
import Select from "react-select";

const Controller = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamATeam, setTeamATeam] = useState(null);
  const [teamBTeam, setTeamBTeam] = useState(null);
  const [formData, setFormData] = useState({
    court: "",
    category: "",
    bonusPoints: 2,
    teamAPlayer1: "",
    teamAPlayer2: "",
    teamBPlayer1: "",
    teamBPlayer2: "",
    matchTag: "normal",
  });

  // Map categories to skill levels
  const getSkillLevelForCategory = (category) => {
    const categorySkillMap = {
      Intermediate: "Intermediate",
      Advanced: "Advanced",
    };
    return categorySkillMap[category];
  };

  // Fetch teams from backend
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await axios.get(
          "https://api-third-shot-score-684526581556.asia-south1.run.app/teams"
        );
        if (response.data.success) {
          setTeams(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
        toast.error("Failed to fetch teams");
      }
    };

    fetchTeams();
  }, []);

  // Fetch players from backend
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await axios.get(
          "https://api-third-shot-score-684526581556.asia-south1.run.app/players"
        );
        if (response.data.success) {
          setPlayers(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching players:", error);
        toast.error("Failed to fetch players");
      }
    };

    fetchPlayers();
  }, []);

  // Filter players based on selected category
  // useEffect(() => {
  //   if (formData.category) {
  //     const requiredSkillLevel = getSkillLevelForCategory(formData.category);
  //     const filtered = players.filter(
  //       (player) => player.skillLevel === requiredSkillLevel
  //     );
  //     setFilteredPlayers(filtered);
  //   } else {
  //     setFilteredPlayers([]);
  //   }
  // }, [formData.category, players]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear player selections when category changes
    if (field === "category") {
      setFormData((prev) => ({
        ...prev,
        bonusPoints: 2, // Reset to default
        teamAPlayer1: "",
        teamAPlayer2: "",
        teamBPlayer1: "",
        teamBPlayer2: "",
      }));
    }
  };

  // Handle team selection for Team A
  const handleTeamAChange = (selectedOption) => {
    const teamId = selectedOption?.value || null;
    setTeamATeam(teamId);
    // Clear Team A player selections when team changes
    setFormData((prev) => ({
      ...prev,
      teamAPlayer1: "",
      teamAPlayer2: "",
    }));
  };

  // Handle team selection for Team B
  const handleTeamBChange = (selectedOption) => {
    const teamId = selectedOption?.value || null;
    setTeamBTeam(teamId);
    // Clear Team B player selections when team changes
    setFormData((prev) => ({
      ...prev,
      teamBPlayer1: "",
      teamBPlayer2: "",
    }));
  };

  // Check if selected category is singles
  const isSingles = (formData.category || "").toLowerCase().includes("singles");

  const handleStartMatch = async () => {
    if (
      !formData.category ||
      !formData.court ||
      !formData.matchTag ||
      !formData.teamAPlayer1 ||
      (!isSingles && !formData.teamAPlayer2) ||
      !formData.teamBPlayer1 ||
      (!isSingles && !formData.teamBPlayer2)
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    // Check for duplicate players
    const selectedPlayers = [
      formData.teamAPlayer1,
      !isSingles && formData.teamAPlayer2,
      formData.teamBPlayer1,
      !isSingles && formData.teamBPlayer2,
    ].filter(Boolean); // Filter out falsy values (null/empty string)

    const uniquePlayers = new Set(selectedPlayers);
    if (uniquePlayers.size !== selectedPlayers.length) {
      toast.error("All players must be different");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "https://api-third-shot-score-684526581556.asia-south1.run.app/pickleball/match",
        {
          category: formData.category,
          court: formData.court,
          bonusPoints: formData.bonusPoints,
          teamAPlayer1Id: parseInt(formData.teamAPlayer1),
          teamAPlayer2Id: isSingles ? null : parseInt(formData.teamAPlayer2),
          teamBPlayer1Id: parseInt(formData.teamBPlayer1),
          teamBPlayer2Id: isSingles ? null : parseInt(formData.teamBPlayer2),
          matchTag: formData.matchTag,
        }
      );
      if (response.data.success) {
        setLoading(false);
        toast.success("Match created successfully!");
        navigate(`/live-match/${response.data.data.matchId}`);
      }
    } catch (error) {
      console.error("Error creating match:", error);
      setLoading(false);
      toast.error(error.response?.data?.message || "Failed to create match");
    }
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "48px",
      borderColor: state.isFocused ? "#001e48" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 30, 72, 0.2)" : "none",
      "&:hover": {
        borderColor: "#001e48",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#001e48"
        : state.isFocused
        ? "#f3f4f6"
        : "white",
      color: state.isSelected ? "white" : "#374151",
    }),
  };

  // Get filtered players for Team A
  const getTeamAPlayers = () => {
    if (teamATeam) {
      return players.filter((player) => player.team?.teamId === teamATeam);
    }
    return players;
  };

  // Get filtered players for Team B
  const getTeamBPlayers = () => {
    if (teamBTeam) {
      return players.filter((player) => player.team?.teamId === teamBTeam);
    }
    return players;
  };

  // Prepare player options for react-select
  const getPlayerOptions = (playerList, excludePlayerIds = []) => {
    return playerList
      .filter((player) => !excludePlayerIds.includes(player.playerId))
      .map((player) => ({
        value: player.playerId,
        label: `${player.playerName} (${player.team?.teamName || "No Team"})`,
      }));
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#028F64",
              secondary: "#fff",
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <div
        className="min-h-screen"
        style={{
          background:
            "linear-gradient(99.32deg, rgb(1 30 72) -0.41%, rgb(17 64 132) 93.61%)",
        }}
      >
        {/* Header */}
        <div className="text-white p-6 pb-8">
          <h1 className="text-2xl font-bold text-center mb-3 mt-8">
            Pickleball Live Score
          </h1>
          <p className="text-center text-sm opacity-90 mb-3 mt-4">Powered by</p>
          <div className="flex justify-center mb-8">
            <img src={"https://storage.googleapis.com/pllayer-assests/Logo-white.png"} alt="PLLAYER" className="h-8" />
          </div>
        </div>

        {/* Main Content */}
        <div
          className="bg-white flex-1 p-6"
          style={{ height: "calc(100vh - 160px)" }}
        >
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Enter Match Details
          </h2>

          {/* Select Category */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Select Category <span className="text-red-500">*</span>
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {getAllCategories().map((category) => (
                <button
                  key={category}
                  onClick={() => handleInputChange("category", category)}
                  className={`px-4 py-3 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap flex-shrink-0 ${
                    formData.category === category
                      ? "bg-[#001e48] text-white border-[#001e48]"
                      : "bg-white text-gray-700 border-gray-300 hover:border-[#001e48]"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Select Court */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Select Court <span className="text-red-500">*</span>
            </h3>
            <div className="grid grid-cols-4 gap-2">
              {[
                "court-1",
                "court-2",
                "court-3",
                "court-4",
                "court-5",
                "court-6",
                "court-7",
                "court-8",
              ].map((court) => (
                <button
                  key={court}
                  onClick={() => handleInputChange("court", court)}
                  className={`p-3 text-sm font-medium rounded-lg border transition-colors ${
                    formData.court === court
                      ? "bg-[#001e48] text-white border-[#001e48]"
                      : "bg-white text-gray-700 border-gray-300 hover:border-[#001e48]"
                  }`}
                >
                  {court.replace("court-", "Court ")}
                </button>
              ))}
            </div>
          </div>

          {/* Select Bonus Points */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Bonus Points <span className="text-red-500">*</span>
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 2, label: "2 Points" },
                { value: 3, label: "3 Points" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleInputChange("bonusPoints", option.value)}
                  className={`p-3 text-sm font-medium rounded-lg border transition-colors ${
                    formData.bonusPoints === option.value
                      ? "bg-[#001e48] text-white border-[#001e48]"
                      : "bg-white text-gray-700 border-gray-300 hover:border-[#001e48]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Select Match Tag */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Select Match Type <span className="text-red-500">*</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { value: "normal", label: "Normal" },
                { value: "quarter final", label: "Quarter Final" },
                { value: "semi final", label: "Semi Final" },
                { value: "final", label: "Final" },
              ].map((tag) => (
                <button
                  key={tag.value}
                  onClick={() => handleInputChange("matchTag", tag.value)}
                  className={`p-3 text-sm font-medium rounded-lg border transition-colors ${
                    formData.matchTag === tag.value
                      ? "bg-[#001e48] text-white border-[#001e48]"
                      : "bg-white text-gray-700 border-gray-300 hover:border-[#001e48]"
                  }`}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          </div>

          {/* Team A Players */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Team A Players <span className="text-red-500">*</span>
            </h3>

            {/* Team A Team Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Select Team A <span className="text-gray-500">(Optional)</span>
              </label>
              <Select
                value={
                  teamATeam
                    ? {
                        value: teamATeam,
                        label:
                          teams.find((t) => t.teamId === teamATeam)?.teamName ||
                          "",
                      }
                    : null
                }
                onChange={handleTeamAChange}
                options={[
                  { value: null, label: "All Teams" },
                  ...teams.map((team) => ({
                    value: team.teamId,
                    label: team.teamName,
                  })),
                ]}
                placeholder="Select Team A (or leave blank for all teams)"
                styles={customSelectStyles}
                isClearable
              />
            </div>

            {formData.category && getTeamAPlayers().length === 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  {teamATeam
                    ? `No players found in the selected team. Please select a different team or clear the team filter.`
                    : `No players found. Please add players or select a different category.`}
                </p>
              </div>
            )}
            <div
              className={`grid grid-cols-1 ${
                !isSingles ? "md:grid-cols-2" : ""
              } gap-4`}
            >
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Player 1
                </label>
                <Select
                  value={
                    formData.teamAPlayer1
                      ? (() => {
                          const teamAPlayers = getTeamAPlayers();
                          const player = teamAPlayers.find(
                            (p) =>
                              p.playerId === parseInt(formData.teamAPlayer1)
                          );
                          return player
                            ? {
                                value: formData.teamAPlayer1,
                                label: `${player.playerName} (${
                                  player.team?.teamName || "No Team"
                                })`,
                              }
                            : null;
                        })()
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleInputChange(
                      "teamAPlayer1",
                      selectedOption?.value || ""
                    )
                  }
                  options={getPlayerOptions(getTeamAPlayers(), [
                    parseInt(formData.teamAPlayer2),
                    parseInt(formData.teamBPlayer1),
                    parseInt(formData.teamBPlayer2),
                  ])}
                  placeholder="Select Player 1"
                  isDisabled={!formData.category}
                  styles={customSelectStyles}
                  isClearable
                />
              </div>
              {!isSingles && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Player 2
                  </label>
                  <Select
                    value={
                      formData.teamAPlayer2
                        ? (() => {
                            const teamAPlayers = getTeamAPlayers();
                            const player = teamAPlayers.find(
                              (p) =>
                                p.playerId === parseInt(formData.teamAPlayer2)
                            );
                            return player
                              ? {
                                  value: formData.teamAPlayer2,
                                  label: `${player.playerName} (${
                                    player.team?.teamName || "No Team"
                                  })`,
                                }
                              : null;
                          })()
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleInputChange(
                        "teamAPlayer2",
                        selectedOption?.value || ""
                      )
                    }
                    options={getPlayerOptions(getTeamAPlayers(), [
                      parseInt(formData.teamAPlayer1),
                      parseInt(formData.teamBPlayer1),
                      parseInt(formData.teamBPlayer2),
                    ])}
                    placeholder="Select Player 2"
                    isDisabled={!formData.category}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>
              )}
            </div>
          </div>

          {/* Team B Players */}
          <div className="mb-8">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Team B Players <span className="text-red-500">*</span>
            </h3>

            {/* Team B Team Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Select Team B <span className="text-gray-500">(Optional)</span>
              </label>
              <Select
                value={
                  teamBTeam
                    ? {
                        value: teamBTeam,
                        label:
                          teams.find((t) => t.teamId === teamBTeam)?.teamName ||
                          "",
                      }
                    : null
                }
                onChange={handleTeamBChange}
                options={[
                  { value: null, label: "All Teams" },
                  ...teams.map((team) => ({
                    value: team.teamId,
                    label: team.teamName,
                  })),
                ]}
                placeholder="Select Team B (or leave blank for all teams)"
                styles={customSelectStyles}
                isClearable
              />
            </div>

            {formData.category && getTeamBPlayers().length === 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  {teamBTeam
                    ? `No players found in the selected team. Please select a different team or clear the team filter.`
                    : `No players found. Please add players or select a different category.`}
                </p>
              </div>
            )}

            <div
              className={`grid grid-cols-1 ${
                !isSingles ? "md:grid-cols-2" : ""
              } gap-4`}
            >
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Player 1
                </label>
                <Select
                  value={
                    formData.teamBPlayer1
                      ? (() => {
                          const teamBPlayers = getTeamBPlayers();
                          const player = teamBPlayers.find(
                            (p) =>
                              p.playerId === parseInt(formData.teamBPlayer1)
                          );
                          return player
                            ? {
                                value: formData.teamBPlayer1,
                                label: `${player.playerName} (${
                                  player.team?.teamName || "No Team"
                                })`,
                              }
                            : null;
                        })()
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleInputChange(
                      "teamBPlayer1",
                      selectedOption?.value || ""
                    )
                  }
                  options={getPlayerOptions(getTeamBPlayers(), [
                    parseInt(formData.teamAPlayer1),
                    parseInt(formData.teamAPlayer2),
                    parseInt(formData.teamBPlayer2),
                  ])}
                  placeholder="Select Player 1"
                  isDisabled={!formData.category}
                  styles={customSelectStyles}
                  isClearable
                />
              </div>
              {!isSingles && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Player 2
                  </label>
                  <Select
                    value={
                      formData.teamBPlayer2
                        ? (() => {
                            const teamBPlayers = getTeamBPlayers();
                            const player = teamBPlayers.find(
                              (p) =>
                                p.playerId === parseInt(formData.teamBPlayer2)
                            );
                            return player
                              ? {
                                  value: formData.teamBPlayer2,
                                  label: `${player.playerName} (${
                                    player.team?.teamName || "No Team"
                                  })`,
                                }
                              : null;
                          })()
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleInputChange(
                        "teamBPlayer2",
                        selectedOption?.value || ""
                      )
                    }
                    options={getPlayerOptions(getTeamBPlayers(), [
                      parseInt(formData.teamAPlayer1),
                      parseInt(formData.teamAPlayer2),
                      parseInt(formData.teamBPlayer1),
                    ])}
                    placeholder="Select Player 2"
                    isDisabled={!formData.category}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>
              )}
            </div>
          </div>

          {/* Start Match Button */}
          <button
            onClick={handleStartMatch}
            style={{ marginBottom: "30px" }}
            disabled={
              !formData.court ||
              !formData.category ||
              !formData.matchTag ||
              !formData.teamAPlayer1 ||
              (!isSingles && !formData.teamAPlayer2) ||
              !formData.teamBPlayer1 ||
              (!isSingles && !formData.teamBPlayer2) ||
              loading
            }
            className="w-full bg-[#001e48] text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-[#7a1f9e] transition-colors"
          >
            {loading ? "Creating..." : "Start Match"}
          </button>
        </div>
      </div>
    </>
  );
};

export default Controller;
